/**
 * Conversión de la URL que carga el productor a una URL incrustable en iframe.
 *
 * Por qué existe esto: el productor copia la URL de la barra del navegador
 * (`youtube.com/watch?v=XXX`) y ESA url NO entra en un iframe — YouTube la
 * sirve con `X-Frame-Options: SAMEORIGIN` y el visitante ve un cuadro en
 * blanco. La que se incrusta es `youtube.com/embed/XXX`. Lo mismo con Vimeo
 * y Facebook. Sin esta traducción, la feature "anda" en el editor y falla en
 * la única pantalla que importa.
 */

// 11 caracteres exactos, el largo de un ID de video de YouTube.
const YT_ID = /^[\w-]{11}$/;

const VIMEO_ID = /^\d+$/;

/** URL válida o null (sin tirar excepción, que es lo que hace `new URL`). */
const parsear = (texto) => {
  const limpio = String(texto || '').trim();
  if (!limpio) return null;
  // Sin esquema, `new URL` falla. El productor pega "youtube.com/watch?v=X"
  // tanto como la URL completa.
  const conEsquema = /^https?:\/\//i.test(limpio) ? limpio : `https://${limpio}`;
  try {
    return new URL(conEsquema);
  } catch {
    return null;
  }
};

/**
 * Si el productor pegó el CÓDIGO de incrustación completo (`<iframe …>`) en
 * vez de la URL, sacamos el src. Muchos servicios de streaming entregan el
 * embed y no una URL suelta, así que pegarlo es lo natural.
 *
 * Se extrae el src y se arma un iframe propio: el HTML del productor NUNCA se
 * inyecta en la página. Aunque sea un usuario autenticado del tenant, meter
 * markup ajeno con dangerouslySetInnerHTML es un XSS servido en bandeja.
 */
const srcDeIframe = (texto) => {
  const m = /<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["']/i.exec(String(texto || ''));
  return m ? m[1] : null;
};

/**
 * @param {string} entrada  URL o código de incrustación cargado por el productor.
 * @returns {{url: string, proveedor: string} | null}
 *          `null` = no se pudo traducir a algo incrustable. El llamador
 *          muestra el link externo como plan B en vez de un iframe roto.
 */
export function urlEmbedStreaming(entrada) {
  const desdeIframe = srcDeIframe(entrada);
  const u = parsear(desdeIframe || entrada);
  if (!u) return null;

  const host = u.hostname.replace(/^www\./i, '').toLowerCase();
  const partes = u.pathname.split('/').filter(Boolean);

  // ── YouTube ──────────────────────────────────────────────────────────────
  if (host === 'youtu.be') {
    const id = partes[0];
    return YT_ID.test(id) ? { url: `https://www.youtube.com/embed/${id}`, proveedor: 'YouTube' } : null;
  }

  if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'm.youtube.com') {
    // /watch?v=ID
    const v = u.searchParams.get('v');
    if (v && YT_ID.test(v)) {
      return { url: `https://www.youtube.com/embed/${v}`, proveedor: 'YouTube' };
    }
    // /embed/ID, /live/ID, /shorts/ID, /v/ID — todas llevan el ID en la 2da parte.
    if (['embed', 'live', 'shorts', 'v'].includes(partes[0]) && YT_ID.test(partes[1])) {
      return { url: `https://www.youtube.com/embed/${partes[1]}`, proveedor: 'YouTube' };
    }
    // La transmisión de un canal, sin video puntual: /channel/ID/live. No hay
    // ID de video que extraer, pero YouTube expone el embed por canal.
    // `youtube.com/@handle/live` NO entra acá: live_stream necesita el ID del
    // canal (UC...) y el handle no se puede resolver sin llamar a la API de
    // YouTube. Cae en null y el visitante recibe el link externo.
    if (partes[partes.length - 1] === 'live' && partes[0] === 'channel' && partes[1]) {
      return {
        url: `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(partes[1])}`,
        proveedor: 'YouTube',
      };
    }
    return null;
  }

  // ── Vimeo ────────────────────────────────────────────────────────────────
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    // player.vimeo.com/video/ID ya es la forma incrustable.
    const idx = partes.indexOf('video');
    const id = idx >= 0 ? partes[idx + 1] : partes[0];
    if (!VIMEO_ID.test(id || '')) return null;
    // Los videos privados de Vimeo llevan un hash en la URL; sin él, el
    // player devuelve 403.
    const hash = idx >= 0 ? u.searchParams.get('h') : partes[1];
    const qs = hash && /^[\w]+$/.test(hash) ? `?h=${hash}` : '';
    return { url: `https://player.vimeo.com/video/${id}${qs}`, proveedor: 'Vimeo' };
  }

  // ── Facebook ─────────────────────────────────────────────────────────────
  if (host === 'facebook.com' || host === 'fb.watch' || host === 'web.facebook.com') {
    // El plugin ya viene armado (es lo que devuelve srcDeIframe con el embed
    // que copia el productor desde Facebook).
    if (u.pathname.startsWith('/plugins/video.php')) {
      return { url: u.toString(), proveedor: 'Facebook' };
    }
    // Solo si el path ES un video. Envolver cualquier URL de facebook.com en
    // el plugin da un embed que carga en blanco, y como devolvía un objeto
    // igual, el editor pintaba "link válido" en verde sobre un reproductor
    // muerto — justo lo que el preview existe para evitar. Un perfil o una
    // página tienen que caer en null para que se ofrezca el link externo.
    const esVideo = (
      host === 'fb.watch'                               // short link, siempre a un video
      || /^\/watch\/?$/.test(u.pathname)                // /watch?v=ID
      || /^\/video\.php$/.test(u.pathname)              // /video.php?v=ID
      || /^\/reel\/\d+/.test(u.pathname)                // /reel/ID
      || /^\/[^/]+\/videos\/[^/]+/.test(u.pathname)     // /pagina/videos/ID
    );
    if (!esVideo) return null;
    return {
      url: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(u.toString())}&show_text=false`,
      proveedor: 'Facebook',
    };
  }

  // ── Cualquier otro origen que ya sea un embed ────────────────────────────
  // Si vino de un <iframe> que el productor copió del proveedor, ese src es
  // por definición incrustable — sea Twitch, Dailymotion o el panel de su
  // propio streaming. Una URL suelta de un dominio desconocido no: casi
  // siempre es la página del reproductor, no el reproductor.
  if (desdeIframe && u.protocol === 'https:') {
    return { url: u.toString(), proveedor: host };
  }

  return null;
}

/**
 * URL normalizada para abrir en una pestaña nueva (plan B del iframe).
 *
 * Es lo que decide si el micrositio anuncia la transmisión, así que exige un
 * dominio de verdad y no solo algo que `new URL` acepte: `new URL` da por
 * buena a "https://asdasd" (hostname sin punto), y con eso un typo del
 * productor le pondría al visitante un botón que no lleva a ningún lado.
 */
export function urlExternaStreaming(entrada) {
  // Todo lo incrustable anuncia, sin excepción. El banner es la ÚNICA puerta a
  // /remate/:slug/en-vivo, así que esta función tiene que ser un superconjunto
  // de urlEmbedStreaming: si fuera más estricta, habría un reproductor que
  // funciona al que nadie puede llegar. Pasaba con un embed genérico servido
  // desde una IP o un host sin TLD (`<iframe src="https://190.55.12.4/live">`):
  // el editor lo daba por válido y reproducía en el preview, y el día del
  // remate el micrositio no mostraba nada.
  const embed = urlEmbedStreaming(entrada);
  if (embed) return embed.url;

  const u = parsear(srcDeIframe(entrada) || entrada);
  if (!u) return null;
  // El TLD se exige solo sobre una URL suelta, que es el caso que el guard
  // vino a cubrir: que un typo tipo "asdasd" no dispare un banner con un botón
  // que no lleva a ningún lado.
  if (!/\.[a-z]{2,}$/i.test(u.hostname)) return null;
  return u.toString();
}
